import React from "react";
import { updateNameCompletion } from "../../api/petFormulaApi";
import { NameCompletionRowData } from "../../components/SettingsPage/types";

export const NC_WEIGHT_KEYS = [
  "subbrand_restaurant",
  "volume_restaurant",
  "packaging_type_restaurant",
  "packaging_size_restaurant",
  "subbrand_grocery",
  "volume_grocery",
  "packaging_type_grocery",
  "packaging_size_grocery",
] as const;

const RESTAURANT_KEYS = [
  "subbrand_restaurant",
  "volume_restaurant",
  "packaging_type_restaurant",
  "packaging_size_restaurant",
] as const;

const GROCERY_KEYS = [
  "subbrand_grocery",
  "volume_grocery",
  "packaging_type_grocery",
  "packaging_size_grocery",
] as const;

export const validateGroupSums = (row: Partial<NameCompletionRowData>): {
  restaurantSum: number;
  grocerySum: number;
  restaurantValid: boolean;
  groceryValid: boolean;
} => {
  const restaurantSum = RESTAURANT_KEYS.reduce(
    (sum, k) => sum + (typeof row[k] === "number" ? (row[k] as number) : 0),
    0
  );
  const grocerySum = GROCERY_KEYS.reduce(
    (sum, k) => sum + (typeof row[k] === "number" ? (row[k] as number) : 0),
    0
  );
  return {
    restaurantSum,
    grocerySum,
    restaurantValid: Math.abs(restaurantSum - 100) < 0.1,
    groceryValid: Math.abs(grocerySum - 100) < 0.1,
  };
};

interface HandlerParams {
  tableData: NameCompletionRowData[];
  setTableData: (data: NameCompletionRowData[]) => void;
  editingData: { [key: string]: Partial<NameCompletionRowData> };
  setEditingData: (data: { [key: string]: Partial<NameCompletionRowData> }) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  setIsPipelineRunning: (running: boolean) => void;
  messageApi: any;
  userData: any;
  selectedWorkspace: any;
}

export const createNameCompletionHandlers = ({
  tableData,
  setTableData,
  editingData,
  setEditingData,
  setIsEditing,
  setIsPipelineRunning,
  messageApi,
  userData,
  selectedWorkspace,
}: HandlerParams) => {
  const validateData = (): boolean => {
    const mergedRows = tableData.map((row) => ({
      ...row,
      ...(editingData[row.key] ?? {}),
    }));
    for (const row of mergedRows) {
      const { restaurantValid, groceryValid, restaurantSum, grocerySum } =
        validateGroupSums(row);
      if (!restaurantValid) {
        const rounded = Math.round(restaurantSum * 100) / 100;
        messageApi.error({
          key: "nc-validation",
          content: `Restaurant weights for ${row.market} must sum to 100%. Current: ${rounded}%.`,
          duration: 5,
        });
        return false;
      }
      if (!groceryValid) {
        const rounded = Math.round(grocerySum * 100) / 100;
        messageApi.error({
          key: "nc-validation",
          content: `Grocery weights for ${row.market} must sum to 100%. Current: ${rounded}%.`,
          duration: 5,
        });
        return false;
      }
    }
    return true;
  };

  const handleEnterEdit = () => {
    const initialEditing: { [key: string]: Partial<NameCompletionRowData> } = {};
    tableData.forEach((row) => {
      const weights: Partial<NameCompletionRowData> = {};
      NC_WEIGHT_KEYS.forEach((k) => { (weights as any)[k] = row[k]; });
      initialEditing[row.key] = weights;
    });
    setEditingData(initialEditing);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditingData({});
    setIsEditing(false);
  };

  const handleInputChange = (
    key: string,
    field: keyof NameCompletionRowData,
    value: number
  ) => {
    const clamped = Math.max(0, Math.min(100, value || 0));
    setEditingData({
      ...editingData,
      [key]: { ...(editingData[key] ?? {}), [field]: clamped },
    } as { [key: string]: Partial<NameCompletionRowData> });
  };

  const handleSaveDefaultValue = (key: string) => {
    const sourceRow = tableData.find((r) => r.key === key);
    if (!sourceRow) return;
    const merged = { ...sourceRow, ...(editingData[key] ?? {}) };
    const { restaurantValid, groceryValid, restaurantSum, grocerySum } = validateGroupSums(merged);
    if (!restaurantValid) {
      messageApi.error({ key: "nc-save-default", content: `Restaurant weights must sum to 100%. Current: ${Math.round(restaurantSum * 100) / 100}%.`, duration: 5 });
      return;
    }
    if (!groceryValid) {
      messageApi.error({ key: "nc-save-default", content: `Grocery weights must sum to 100%. Current: ${Math.round(grocerySum * 100) / 100}%.`, duration: 5 });
      return;
    }
    const updated = tableData.map((r) => (r.key === key ? { ...r, ...merged } : r));
    setTableData(updated);
    const newEditing = { ...editingData };
    delete newEditing[key];
    setEditingData(newEditing);
    messageApi.success({ key: "nc-save-default", content: "Default values saved. Use 'Apply to All' to copy to all countries.", duration: 3 });
  };

  const handleApplyDefaultToAll = () => {
    const defaultRow = tableData.find((r) => r.key === "default-value");
    if (!defaultRow) return;
    const mergedDefault = { ...defaultRow, ...(editingData["default-value"] ?? {}) };
    const { restaurantValid, groceryValid } = validateGroupSums(mergedDefault);
    if (!restaurantValid || !groceryValid) {
      messageApi.error({ key: "nc-apply-all", content: "Default values don't sum to 100%. Save valid defaults first.", duration: 5 });
      return;
    }
    const defaultWeights: Partial<NameCompletionRowData> = {};
    NC_WEIGHT_KEYS.forEach((k) => { (defaultWeights as any)[k] = mergedDefault[k]; });
    const newEditing: { [key: string]: Partial<NameCompletionRowData> } = {};
    for (const rowKey of Object.keys(editingData)) {
      newEditing[rowKey] = { ...(editingData[rowKey] ?? {}), ...defaultWeights };
    }
    setEditingData(newEditing);
    messageApi.success({ key: "nc-apply-all", content: "Default values applied to all countries.", duration: 3 });
  };

  const handleApplyDefaultToEditingRow = (key: string) => {
    const defaultRow = tableData.find((r) => r.key === "default-value");
    if (!defaultRow) return;
    const mergedDefault = { ...defaultRow, ...(editingData["default-value"] ?? {}) };
    const defaultWeights: Partial<NameCompletionRowData> = {};
    NC_WEIGHT_KEYS.forEach((k) => { (defaultWeights as any)[k] = mergedDefault[k]; });
    setEditingData({
      ...editingData,
      [key]: { ...(editingData[key] ?? {}), ...defaultWeights },
    } as { [key: string]: Partial<NameCompletionRowData> });
    const row = tableData.find((r) => r.key === key);
    messageApi.success({ key: `nc-apply-default-editing-${key}`, content: `Default values applied to ${row?.market ?? key} editing fields.`, duration: 2 });
  };

  const handleApplyDefaultToRow = async (key: string) => {
    const defaultRow = tableData.find((r) => r.key === "default-value");
    const targetRow = tableData.find((r) => r.key === key);
    if (!defaultRow || !targetRow) return;
    const defaultWeights: Record<string, number> = {};
    NC_WEIGHT_KEYS.forEach((k) => { defaultWeights[k] = defaultRow[k] as number; });
    const requestBody = {
      countries: [{ country: key, weights: defaultWeights }],
      comment: "Reverted to default weights",
    };
    try {
      messageApi.loading({ key: "nc-apply-default", content: "Applying default values...", duration: 0 });
      await updateNameCompletion(requestBody, userData, selectedWorkspace);
      setIsPipelineRunning(true);
      const updated = tableData.map((r) =>
        r.key === key
          ? { ...r, ...defaultWeights, is_default: true, editBy: undefined, updated_at: undefined }
          : r
      );
      setTableData(updated);
      messageApi.success({ key: "nc-apply-default", content: "Default values applied. Recalculating scores...", duration: 5 });
    } catch (error: any) {
      messageApi.error({ key: "nc-apply-default", content: `Failed to apply default: ${error.message}`, duration: 5 });
    }
  };

  return {
    handleEnterEdit,
    handleCancel,
    handleInputChange,
    handleSaveDefaultValue,
    handleApplyDefaultToAll,
    handleApplyDefaultToEditingRow,
    handleApplyDefaultToRow,
    validateData,
  };
};
